<template>
  <div class="coord-page">

    <!-- 좌측 PDF 목록 -->
    <div class="sidebar">
      <h3>PDF 목록</h3>

      <div
        v-for="t in templates"
        :key="t.code"
        class="pdf-item"
        :class="{ active: selectedCode === t.code }"
        @click="loadTemplate(t)"
      >
        {{ t.code }}
      </div>
    </div>

    <!-- 중앙 PDF 영역 -->
    <div class="pdf-area">

      <div class="page-nav">
        <button @click="prevPage">◀</button>
        <span>{{ currentPage }} / {{ totalPages }}</span>
        <button @click="nextPage">▶</button>

        <div v-if="selectedCode">
          {{ selectedCode }}.pdf
        </div>
      </div>

      <!-- 🔥 PDF 스테이지 -->
      <div class="pdf-wrapper">
        <div
          class="pdf-stage"
          :style="{
            width: canvasWidth + 'px',
            height: canvasHeight + 'px'
          }"
        >

          <!-- PDF Canvas -->
          <canvas ref="pdfCanvas" class="pdf-canvas"></canvas>

          <!-- 🔥 오버레이 레이어 -->
          <div class="overlay-layer">
            <vue-draggable-resizable
              v-for="(box, index) in currentBoxes"
              :key="index"
              :x="box.x"
              :y="box.y"
              :w="box.w"
              :h="box.h"
              :parent="true"
              @drag-stop="(left, top) => updatePosition(index, left, top)"
              @resize-stop="(left, top, width, height) => updateSize(index, left, top, width, height)"
            >
              <div class="box-inner">
                {{ box.field }}
                <span class="delete-btn" @click.stop="removeBox(index)">✕</span>
              </div>
            </vue-draggable-resizable>
          </div>

        </div>
      </div>

    </div>

    <!-- 우측 패널 -->
    <div class="right-panel">
      <h3>양식 PDF</h3>
      <div class="admin-row">
        <input v-model="adminToken" type="password" class="token-box" placeholder="관리 토큰" @change="saveToken" />
      </div>
      <div class="admin-row">
        <input ref="pdfInput" type="file" accept="application/pdf" style="display:none" @change="onPdfPicked" />
        <button :disabled="uploading || selectedCode === null" @click="pickPdf">
          {{ uploading ? "업로드 중..." : "이 양식 교체" }}
        </button>
      </div>
      <hr />
      <h3>필드 추가</h3>

      <button
        v-for="f in fieldList"
        :key="f"
        @click="addField(f)"
      >
        {{ f }}
      </button>

      <hr />

      <h3>JSON</h3>

      <textarea v-model="jsonText" class="json-box"></textarea>
      
      <div>
        <button @click="applyJson">JSON 불러오기</button>
        <button @click="applyCurrentPageToJson">현재 페이지 반영</button>
        <button @click="saveJson">저장</button>
      </div>
      
      <h4>필드 단위 반영</h4>
      <div v-for="f in fieldList" :key="'apply_'+f">
        <button @click="applyFieldToJson(f)">
          {{ f }} 반영
        </button>
      </div>
    </div>

  </div>
  <transition name="glass-toast">
    <div v-if="showToast" class="glass-toast">
      <div class="glass-inner">
        <span class="glass-icon">✓</span>
        <span class="glass-text">{{ toastMessage }}</span>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue"
import VueDraggableResizable from "vue-draggable-resizable"
import "vue-draggable-resizable/style.css"
import * as pdfjsLib from "pdfjs-dist"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js"

const templates = ref([])
const selectedCode = ref(null)
const pdfCanvas = ref(null)
const isDirty = ref(false)

let pdfDoc = null
let currentViewport = null

const currentPage = ref(1)
const totalPages = ref(1)

const canvasHeight = ref(0)
const canvasWidth = ref(0)

const currentBoxes = reactive([])
const jsonText = ref("")
const loadedJson = ref(null)

const isRendering = ref(false)
const scale = 1.5

const toastMessage = ref("")
const showToast = ref(false)
const adminToken = ref(localStorage.getItem("claimAdminToken") || "")
const pdfInput = ref(null)
const uploading = ref(false)

const fieldList = [
  "청구인","주민앞뒤","주민앞","주민뒤","주소1","주소2","직업",
  "사고유형","사고내용","사고주소",
  "은행","계좌","예금주","연락처",
  "수익자이름","수익자주민앞뒤","수익자주민앞","수익자주민뒤",
  "의료급여수급권자","자동이체동일",
  "서명","수익자서명",
  "체크" , "사고유형_질병_체크", "사고유형_일반상해_체크" , "사고유형_교통사고_체크",
  "사고일자","사고일자_년", "사고일자_월", "사고일자_일",
  "접수일자","접수일자_년","접수일자_월", "접수일자_일",
  "연락처1", "연락처2", "연락처3","연락처23", "추가청구"
    
]

const defaultFontSize = {
  연락처: 15,
  연락처1: 15,
  연락처2: 15,
  연락처3: 15,
  연락처23: 15,
  체크: 15
}

function saveToken() {
  localStorage.setItem("claimAdminToken", adminToken.value || "")
}

function authHeaders(extra) {
  const h = extra || {}
  if (adminToken.value) h["X-Admin-Token"] = adminToken.value
  return h
}

function pickPdf() {
  if (selectedCode.value && pdfInput.value) pdfInput.value.click()
}

async function onPdfPicked(e) {
  const file = e.target.files && e.target.files[0]
  e.target.value = ""
  if (file) {
    const go = confirm(selectedCode.value + ".pdf 를 이 파일로 교체합니다. 기존 양식은 서버에 백업됩니다. 계속할까요?")
    if (go) {
      uploading.value = true
      try {
        const fd = new FormData()
        fd.append("file", file)
        const res = await fetch("/api/templates/" + selectedCode.value, {
          method: "POST",
          headers: authHeaders(),
          body: fd
        })
        const json = await res.json()
        if (json.ok) {
          const expected = loadedJson.value && loadedJson.value.page_all
          if (expected && json.pages === expected) {
            showSuccess("양식 교체 완료")
          } else if (expected) {
            alert("교체는 됐지만 페이지 수가 다릅니다. 좌표 기준 " + expected + "쪽 → 새 양식 " + json.pages + "쪽. 좌표를 다시 잡아야 합니다.")
          } else {
            showSuccess("양식 교체 완료")
          }
          await loadTemplate({
            code: selectedCode.value,
            pdf_url: "/api/templates/" + selectedCode.value + ".pdf?t=" + Date.now()
          })
        } else {
          alert("교체 실패: " + (json.error || res.status))
        }
      } catch (err) {
        alert("교체 실패: " + err.message)
      } finally {
        uploading.value = false
      }
    }
  }
}
async function fetchTemplates() {
  const res = await fetch("/api/templates")
  templates.value = await res.json()
}

async function loadTemplate(t) {
  selectedCode.value = t.code
  currentPage.value = 1
  await loadCoords()
  await loadPdf(t.pdf_url)
}

async function loadPdf(url) {
  if (pdfDoc && typeof pdfDoc.destroy === "function") {
      pdfDoc.destroy()
      pdfDoc = null
    }

  const loadingTask = pdfjsLib.getDocument(url)
  pdfDoc = await loadingTask.promise
  totalPages.value = pdfDoc.numPages
  await renderPage()
}

async function renderPage() {
  if (!pdfDoc || isRendering.value) return
  isRendering.value = true

  const page = await pdfDoc.getPage(currentPage.value)
  currentViewport = page.getViewport({ scale })

  const canvas = pdfCanvas.value
  const context = canvas.getContext("2d")

  canvas.width = currentViewport.width
  canvas.height = currentViewport.height

  canvasWidth.value = currentViewport.width
  canvasHeight.value = currentViewport.height

  await page.render({
    canvasContext: context,
    viewport: currentViewport
  }).promise

  loadBoxesForPage()
  isRendering.value = false
}

async function loadCoords() {
  const res = await fetch(`/api/coords/${selectedCode.value}`)
  const data = await res.json()

  loadedJson.value = data
  jsonText.value = JSON.stringify(data, null, 2)
}

function loadBoxesForPage() {
  currentBoxes.splice(0)

  if (!loadedJson.value) return

  const pageData =
    loadedJson.value.pages?.[String(currentPage.value)] || {}

  for (const key in pageData) {
    const value = pageData[key]

    if (Array.isArray(value)) {
      value.forEach(pos => pushBox(key, pos))
    } else if (typeof value === "object" && value !== null) {
      pushBox(key, value)
    }
  }
}

function round2(value) {
  return Math.round(value * 100) / 100
}

function pushBox(field, pos) {
  const w = (pos.w || 120) * scale
  const h = (pos.h || 30) * scale

  const x = pos.x * scale
  const y = currentViewport.height - (pos.y * scale) - h

  currentBoxes.push({
    field,
    x,
    y,
    w,
    h,
    size: pos.size || defaultFontSize[field] || 10
  })
}

function browserToPdfX(x) {
  return x / scale
}

function browserToPdfY(y, h) {
  return (currentViewport.height - y - h) / scale
}

function addField(field) {
  currentBoxes.push({
    field,
    x: 100,
    y: 100,
    w: field.includes("서명") ? 200 : 150,
    h: field.includes("서명") ? 80 : 40,
    size: defaultFontSize[field] || 10   
  })
  //syncJson()
  isDirty.value = true
}

function removeBox(index) {
  currentBoxes.splice(index, 1)
  isDirty.value = true
}

function updatePosition(index, left, top) {
  currentBoxes[index].x = left
  currentBoxes[index].y = top
  isDirty.value = true
  console.log("drag updated:", currentBoxes[index])
}


function updateSize(index, left, top, width, height) {
  currentBoxes[index].x = left
  currentBoxes[index].y = top
  currentBoxes[index].w = width
  currentBoxes[index].h = height
  isDirty.value = true
  console.log("resize updated:", currentBoxes[index])
}

function applyCurrentPageToJson() {
  if (!loadedJson.value) return

  const pageKey = String(currentPage.value)

  // 깊은 복사 (매번 새 객체 생성)
  const newJson = JSON.parse(JSON.stringify(loadedJson.value))

  if (!newJson.pages) newJson.pages = {}
  newJson.pages[pageKey] = {}

  currentBoxes.forEach(box => {
    if (!newJson.pages[pageKey][box.field]) {
      newJson.pages[pageKey][box.field] = []
    }

    newJson.pages[pageKey][box.field].push({
      x: round2(browserToPdfX(box.x)),
      y: round2(browserToPdfY(box.y, box.h)),
      w: round2(browserToPdfX(box.w)),
      h: round2(box.h / scale),
      size: box.size || defaultFontSize[box.field] || 10
    })
  })

  loadedJson.value = newJson
  jsonText.value = JSON.stringify(newJson, null, 2)

  isDirty.value = false
  showSuccess("현재 페이지 반영 완료")
}

/*function syncJson() {
  if (!loadedJson) return
  const pageKey = String(currentPage.value)
  loadedJson.pages[pageKey] = {}

  currentBoxes.forEach(box => {
    if (!loadedJson.pages[pageKey][box.field]) {
      loadedJson.pages[pageKey][box.field] = []
    }

    loadedJson.pages[pageKey][box.field].push({
      x: browserToPdfX(box.x),
      y: browserToPdfY(box.y, box.h),
      w: browserToPdfX(box.w),
      h: box.h / scale,
      size: 10
    })
  })

  jsonText.value = JSON.stringify(loadedJson, null, 2)
}*/

function showSuccess(message) {
  toastMessage.value = message
  showToast.value = true

  setTimeout(() => {
    showToast.value = false
  }, 2000)
}

function applyJson() {
  loadedJson.value = JSON.parse(jsonText.value)
  renderPage()
}

async function saveJson() {
  if (isDirty.value) {
    const ok = confirm("반영되지 않은 변경사항이 있습니다. 저장하시겠습니까?")
    if (!ok) return
  }

  await fetch(`/api/coords/${selectedCode.value}`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: jsonText.value
  })

  showSuccess("저장 완료")
}

async function nextPage() {
  if (isDirty.value) {
    const ok = confirm("현재 페이지 변경사항이 반영되지 않았습니다. 이동하시겠습니까?")
    if (!ok) return
  }

  if (currentPage.value >= totalPages.value || isRendering.value) return
  currentPage.value++
  await renderPage()
  isDirty.value = false
}

async function prevPage() {
  if (isDirty.value) {
    const ok = confirm("현재 페이지 변경사항이 반영되지 않았습니다. 이동하시겠습니까?")
    if (!ok) return
  }

  if (currentPage.value <= 1 || isRendering.value) return
  currentPage.value--
  await renderPage()
  isDirty.value = false
}

function applyFieldToJson(fieldName) {
  if (!loadedJson.value) return

  const pageKey = String(currentPage.value)

  const newJson = JSON.parse(JSON.stringify(loadedJson.value))

  if (!newJson.pages) newJson.pages = {}
  if (!newJson.pages[pageKey]) newJson.pages[pageKey] = {}

  newJson.pages[pageKey][fieldName] = []

  currentBoxes
    .filter(b => b.field === fieldName)
    .forEach(box => {
      newJson.pages[pageKey][fieldName].push({
        x: round2(browserToPdfX(box.x)),
        y: round2(browserToPdfY(box.y, box.h)),
        w: round2(browserToPdfX(box.w)),
        h: round2(box.h / scale),
        size: box.size
      })
    })

  loadedJson.value = newJson
  jsonText.value = JSON.stringify(newJson, null, 2)

  isDirty.value = false
}

onMounted(fetchTemplates)
</script>

<style scoped>
.admin-row {
  margin-bottom: 6px;
}

.token-box {
  width: 100%;
  box-sizing: border-box;
  padding: 4px 6px;
  font-size: 12px;
}
.coord-page {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

/* 좌측 목록 */
.sidebar {
  width: 220px;
  border-right: 1px solid #ddd;
  padding: 10px;
  overflow-y: auto;
}

/* 중앙 PDF 영역 */
.pdf-area {
  flex: 1;
  background: #f3f3f3;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.page-nav {
  padding: 10px;
  flex: none;
}

/* 🔥 PDF 전체 감싸는 래퍼 */
.pdf-wrapper {
  display: flex;
  justify-content: center;
  flex: none;
}

/* 🔥 PDF + Overlay 기준 좌표계 */
.pdf-stage {
  position: relative;
  flex: none;
}

/* PDF 캔버스 */
.pdf-canvas {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: block;
}

/* 🔥 오버레이 레이어 */
.overlay-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
}

:deep(.overlay-layer .vdr),
:deep(.overlay-layer .vue-draggable-resizable) {
  border: 2px dashed #ff2b2b !important;
  color: #ff2b2b !important;
  background: rgba(255,0,0,0.06) !important;
}

/* 리사이즈 핸들까지 빨갛게 */
:deep(.overlay-layer .vdr-handle) {
  background: #ff2b2b !important;
  border-color: #ff2b2b !important;
}

.vue-draggable-resizable:hover {
  background: rgba(255, 0, 0, 0.08);
}

/* 우측 패널 */
.right-panel {
  width: 360px;
  border-left: 1px solid #ddd;
  padding: 10px;
  overflow-y: auto;
}

.json-box {
  width: 100%;
  height: 350px;
  font-size: 12px;
}

.pdf-item {
  cursor: pointer;
  padding: 6px;
}

.pdf-item.active {
  background: #3498db;
  color: white;
  border-radius: 4px;
}

:deep(.box-inner) {
  position: relative;
  width: 100%;
  height: 100%;
}

:deep(.delete-btn) {
  position: absolute;
  top: -8px;
  right: -8px;
  background: red;
  color: white;
  font-size: 11px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  text-align: center;
  line-height: 16px;
  cursor: pointer;
}

/* ===============================
   Glass Toast - Blue/Navy Final
   =============================== */

.glass-toast {
  position: fixed;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

/* 캡슐 본체 */
.glass-inner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 32px;
  border-radius: 999px;

  /* 🔥 최종 사용 블루-네이비 그라데이션 */
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.45),
    rgba(17, 24, 139, 0.35)
  );

  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);

  border: 1px solid rgba(255, 255, 255, 0.35);

  box-shadow:
    0 10px 40px rgba(17, 24, 139, 0.35),
    inset 0 1px 1px rgba(255,255,255,0.5);

  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.2px;
}

/* 아이콘 */
.glass-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #111889);

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 14px;
  color: white;

  box-shadow: 0 6px 18px rgba(17, 24, 139, 0.45);
}

/* 하이라이트 (유리 느낌 핵심) */
.glass-inner::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: linear-gradient(
    135deg,
    rgba(255,255,255,0.65),
    rgba(255,255,255,0.05)
  );
  opacity: 0.35;
  pointer-events: none;
}

/* 애니메이션 */
.glass-toast-enter-active,
.glass-toast-leave-active {
  transition: all 0.4s ease;
}

.glass-toast-enter-from {
  opacity: 0;
  transform: translate(-50%, -25px) scale(0.95);
}

.glass-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -25px) scale(0.95);
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>